import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      title: 'Project & Pricing',
      faqs: [
        {
          question: 'What are your typical project rates?',
          answer: `My rates vary based on project complexity, timeline, and scope. Here's a general breakdown:\n\n• Hourly Rate: $75-125/hour\n• Small Projects (1-2 weeks): $5,000-15,000\n• Medium Projects (1-3 months): $15,000-50,000\n• Large Projects (3+ months): $50,000+\n\nI provide detailed quotes after understanding your specific requirements.`
        },
        {
          question: 'What is your minimum project size?',
          answer: `I typically work on projects with a minimum budget of $5,000 and timeline of at least 2 weeks. This ensures I can deliver quality work and provide proper project management, testing, and documentation.`
        },
        {
          question: 'Do you work on equity-only projects?',answer: `While I'm selective about equity arrangements, I do consider them for exceptional opportunities with strong teams and clear market potential. I typically require a combination of equity and upfront payment for such projects.`
        },
        {
          question: 'What payment terms do you offer?',
          answer: `For most projects, I work with a 50% upfront payment and 50% upon completion. For larger projects, I can arrange milestone-based payments. I accept bank transfers, checks, and major payment platforms.`
        }
      ]
    },
    {
      title: 'Technical & Process',
      faqs: [
        {
          question: 'What technologies do you specialize in?',
          answer: `My core expertise includes:\n\n• Frontend: React, Next.js, TypeScript, Tailwind CSS\n• Backend: Node.js, Express, Python, PostgreSQL\n• Cloud: AWS, Vercel, Docker\n• Tools: Git, Figma, Linear, Notion\n\nI'm always learning new technologies based on project needs.`
        },
        {
          question: 'Do you provide ongoing maintenance?',
          answer: `Yes! I offer maintenance packages that include bug fixes, security updates, performance monitoring, and feature enhancements. Maintenance rates are typically 15-20% of the original project cost annually.`
        },
        {
          question: 'How do you handle project communication?',answer: `I believe in transparent, regular communication. You'll receive:\n\n• Weekly progress reports\n• Access to project management tools\n• Regular video check-ins\n• Immediate updates on any blockers\n• Final documentation and handover`
        },
        {
          question: 'Do you work with existing development teams?',
          answer: `Absolutely! I enjoy collaborating with existing teams, whether as a lead developer, specialist consultant, or team member. I adapt to your existing workflows and tools.`
        }
      ]
    },
    {
      title: 'Availability & Timeline',
      faqs: [
        {
          question: 'How far in advance should I book?',
          answer: `I typically book 2-4 weeks in advance, though this varies by season and project size. For urgent projects, I sometimes have availability for rush work at premium rates.`
        },
        {
          question: 'Do you work with international clients?',
          answer: `Yes! I work with clients globally and am experienced with different time zones. I'm based in Eastern Time (UTC-5) but can accommodate meetings across different regions.`
        },
        {
          question: 'What are your working hours?',answer: `My standard hours are Monday-Friday, 9 AM - 6 PM EST. For international clients, I'm flexible with meeting times and can adjust my schedule for critical project phases.`
        },
        {
          question: 'How do you handle project delays?',
          answer: `I build buffer time into all project estimates and communicate proactively about any potential delays. If delays occur due to scope changes or external factors, we'll discuss timeline adjustments transparently.`
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Common questions about working together, pricing, and project processes.
          </p>
        </div>

        <div className="space-y-8">
          {faqCategories?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-card border border-border rounded-2xl p-6 lg:p-8">
              <h3 className="text-xl font-bold text-text-primary mb-6 flex items-center">
                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                  <Icon name="HelpCircle" size={18} className="text-accent" />
                </div>
                {category?.title}
              </h3>

              <div className="space-y-4">
                {category?.faqs?.map((faq, faqIndex) => {
                  const isOpen = openFAQ === `${categoryIndex}-${faqIndex}`;
                  
                  return (
                    <div key={faqIndex} className="border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors duration-300"
                      >
                        <span className="font-semibold text-text-primary pr-4">
                          {faq?.question}
                        </span>
                        <Icon 
                          name={isOpen ? "ChevronUp" : "ChevronDown"} 
                          size={20} 
                          className="text-text-secondary transition-transform duration-300"
                        />
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4 border-t border-border bg-muted/20">
                          <div className="pt-4">
                            {faq?.answer?.split('\n')?.map((line, lineIndex) => (
                              <React.Fragment key={lineIndex}>
                                {line && (
                                  <p className="text-text-secondary leading-relaxed">
                                    {line}
                                  </p>
                                )}
                                {line === '' && <br />}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 text-center bg-card border border-border rounded-2xl p-8">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="MessageCircle" size={32} className="text-accent" />
          </div>
          <h3 className="text-xl font-bold text-text-primary mb-2">
            Still Have Questions?
          </h3>
          <p className="text-text-secondary mb-6">
            Don't see your question here? I'm happy to provide more specific information about your project needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => document.getElementById('contact-forms')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-hover transition-colors duration-300"
            >
              <Icon name="Mail" size={16} />
              <span>Send a Message</span>
            </button>
            <button
              onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center space-x-2 px-6 py-3 border border-border text-text-primary rounded-lg hover:bg-muted transition-colors duration-300"
            >
              <Icon name="Calendar" size={16} />
              <span>Schedule a Call</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;